import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import type { OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import FilterChip from 'LuxKit/Chips/FilterChip'
import { getCurrentDateLabel, getCurrentShortOccupancyLabel } from 'selectors/offerPage/offerPageSelectors'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import ModalContext from 'contexts/ModalContext'
import ModalBase from 'LuxKit/Modal/ModalBase'
import OfferCalendarSidebarDatePicker from '../../Filters/CalendarSidebarPicker/OfferCalendarSidebarDatePicker'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { mobileRoomTestCalendarFilters } from '../FlashHotelFilters.constants'
import noop from 'lib/function/noop'
import FlashMobileStickyHeaderFiltersCapacityModal from './FlashMobileStickyHeaderFiltersCapacityModal'
import FlashMobileStickyHeaderFiltersCharterCalendarModal from './FlashMobileStickyHeaderFiltersCharterCalendarModal'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LineSeatIcon from 'LuxKit/Icons/line/LineSeatIcon'
import CharterFlightOfferAirportModal from 'components/OfferPage/Filters/CharterFlight/CharterFlightOfferAirportModal'
import type { FareClassWithReward } from 'constants/flight'
import useOpenCharterFareClassModal from 'hooks/OfferPage/useOpenCharterFareClassModal'
import { getCharterFareClassDisplayLabel } from 'hooks/Flights/useCharterFlightLabels'
import useCharterFareClassPolicy from 'hooks/Flights/useCharterFareClassPolicy'
import useMaldivesCharterModalFareClasses from 'hooks/Flights/useMaldivesCharterModalFareClasses'
import { useIntl } from 'react-intl'
import useFlashMobileCharterDateLabel from './useFlashMobileCharterDateLabel'

const CenteredFilterChip = styled(FilterChip)`
  justify-content: center;
`

interface MappedProps {
  offer: App.Offer
  dateLabel: string
  occupancyLabel: string
  airport?: App.Airport
  charterBundleRoute?: App.CharterBundleRoute
  fareClass: FareClassWithReward
  priceMode: OfferPagePriceMode
  checkIn?: string
  checkOut?: string
  duration?: number
  rooms?: Array<OfferPageRoom>
}

interface Props {
  onAfterCloseCapacity?: () => void
  ref?: React.RefObject<HTMLDivElement | null>
  // Charter calendar props
  priceMode?: 'total' | 'perPerson'
  onCharterDurationChange?: (nights: number) => void
}

function FlashMobileStickyHeaderFilters(props: Props & MappedProps) {
  const {
    offer,
    dateLabel,
    occupancyLabel,
    airport,
    fareClass,
    charterBundleRoute,
    ref,
    onAfterCloseCapacity = noop,
    priceMode,
    onCharterDurationChange,
    checkIn,
    checkOut,
    duration,
    rooms,
  } = props

  const displayedDateLabel = useFlashMobileCharterDateLabel({
    checkInDate: checkIn,
    checkOutDate: checkOut,
    nights: duration,
    charterBundleRoute,
    rooms,
    fareClass,
    feederOriginAirportCode: airport?.code,
    fallbackLabel: dateLabel,
  })

  const intl = useIntl()
  const dispatch = useOfferPageDispatch()
  const showModal = useContext(ModalContext)
  const capacities = useMemo(() => {
    return offer.packages.flatMap((p) => p.roomRate?.capacities ?? [])
  }, [offer.packages])
  const charterFareClassPolicy = useCharterFareClassPolicy(offer, rooms)
  const allowedFareClassesForModal = useMaldivesCharterModalFareClasses(charterFareClassPolicy.allowedFareClasses)
  const canOpenFareClassModal = allowedFareClassesForModal.length > 1
  const shouldShowFareClassFilter = Boolean(charterBundleRoute) && canOpenFareClassModal

  const defaultCharterDurationHandler = useCallback((nights: number) => {
    // Always dispatch setDuration to update the duration state
    dispatch({
      type: OfferPageActions.setDuration,
      duration: nights,
    })
  }, [dispatch])

  const handleCalendarDurationChange = useCallback((nights: number) => {
    if (onCharterDurationChange) {
      onCharterDurationChange(nights)
      return
    }

    defaultCharterDurationHandler(nights)
  }, [onCharterDurationChange, defaultCharterDurationHandler])

  const onClose = useCallback(() => {
    dispatch({ type: OfferPageActions.closeHeaderFilter })
  }, [dispatch])

  const onCloseCapacity = useCallback(() => {
    onClose()
    onAfterCloseCapacity()
  }, [onClose, onAfterCloseCapacity])

  const onOpenCalendar = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleHeaderFilter, filter: 'checkin' })

    if (charterBundleRoute && fareClass && priceMode) {
      // Charter calendar - modal fetches its own data and dispatches dates internally
      showModal(<FlashMobileStickyHeaderFiltersCharterCalendarModal
        onDurationChange={handleCalendarDurationChange}
        onClose={onClose}
      />)
    } else {
      showModal(<ModalBase mode="drawer">
        <OfferCalendarSidebarDatePicker
          useConfirmDatesFooter
          subFilters={mobileRoomTestCalendarFilters}
          onClose={onClose}
        />
      </ModalBase>)
    }
  }, [showModal, dispatch, onClose, charterBundleRoute, fareClass, priceMode, handleCalendarDurationChange])

  const onOpenCapacity = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleHeaderFilter, filter: 'capacity' })

    showModal(<FlashMobileStickyHeaderFiltersCapacityModal capacities={capacities} offer={offer} />).then(onCloseCapacity)
  }, [dispatch, showModal, capacities, offer, onCloseCapacity])

  const onOpenAirport = useCallback(() => {
    if (charterBundleRoute) {
      dispatch({ type: OfferPageActions.toggleHeaderFilter, filter: 'airport' })
      showModal(<CharterFlightOfferAirportModal />).then(onClose)
    }
  }, [charterBundleRoute, dispatch, showModal, onClose])

  const { canOpen: canOpenFareClass, open: openFareClassModal } = useOpenCharterFareClassModal({
    offer,
    fareClass,
    checkInDate: checkIn,
    checkOutDate: checkOut,
    occupancies: rooms ?? [],
    nights: duration,
    feederOriginAirportCode: airport?.code,
  })

  const onOpenFareClass = useCallback(async() => {
    if (charterBundleRoute && canOpenFareClass) {
      await openFareClassModal()
    }
  }, [charterBundleRoute, canOpenFareClass, openFareClassModal])

  const airportLabel = airport ? intl.formatMessage(
    {
      id: 'N1dOlo3xE4KmZucDxm-C',
      defaultMessage: '{name}, {code}',
    },
    {
      name: airport.name,
      code: airport.code,
    },
  ) : intl.formatMessage({
    id: 'coXHrtWPtTxyESPwJDAJ',
    defaultMessage: 'Departure airport',
  })

  return (
    <HorizontalContainerWithFadedOverflow gap={8} horizontalAlign="stretch" ref={ref}>
      <CenteredFilterChip
        startIcon={<LineCalendarIcon />}
        size="medium"
        onClick={onOpenCalendar}
      >
        {displayedDateLabel || intl.formatMessage({
          id: 'dWvgfKyo1KBBweA5ZmYY',
          defaultMessage: 'Anytime',
        })}
      </CenteredFilterChip>
      {occupancyLabel && <CenteredFilterChip
        startIcon={<LineUsersAltIcon />}
        size="medium"
        onClick={onOpenCapacity}
      >
        {occupancyLabel}
      </CenteredFilterChip>}
      {charterBundleRoute && <>
        <CenteredFilterChip
          startIcon={<LinePlaneDepartureIcon />}
          size="medium"
          onClick={onOpenAirport}
        >
          {airportLabel}
        </CenteredFilterChip>
        {shouldShowFareClassFilter && (
          <CenteredFilterChip
            startIcon={<LineSeatIcon />}
            size="medium"
            onClick={onOpenFareClass}
          >
            {getCharterFareClassDisplayLabel(intl, fareClass)}
          </CenteredFilterChip>
        )}
      </>}
    </HorizontalContainerWithFadedOverflow>
  )
}

export default withOfferPageAndReduxState((state): MappedProps => ({
  offer: state.offer!,
  dateLabel: getCurrentDateLabel(state),
  occupancyLabel: getCurrentShortOccupancyLabel(state),
  charterBundleRoute: state.charterBundleRoute,
  airport: state.airport,
  fareClass: state.fareClass,
  priceMode: state.priceMode,
  rooms: state.rooms,
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  duration: state.duration,
}), FlashMobileStickyHeaderFilters)
